import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from 'firebase/auth'
import { httpsCallable, type Functions } from 'firebase/functions'

/**
 * A borda de autenticação do cliente. Só Google, sem cadastro — quem barra
 * quem não está na allowlist é a blocking function no servidor, não uma
 * checagem aqui (ver Docs/03-Arquitetura/Segurança e regras.md).
 *
 * Deliberadamente sem estado próprio: o estado de sessão vive na UI, em
 * `src/ui/auth/session-state.ts`. Lá são **cinco** estados, não os três que o
 * plano previa — entre autenticado e app existe a espera pelo bootstrap da
 * casa (decisão 58).
 */

export function signInWithGoogle(auth: Auth): Promise<User> {
  const provider = new GoogleAuthProvider()
  return signInWithPopup(auth, provider).then((credential) => credential.user)
}

export function signOutUser(auth: Auth): Promise<void> {
  return firebaseSignOut(auth)
}

/** Devolve a função que cancela a inscrição. O primeiro disparo é o que
 * separa "carregando" de "deslogado" — ver Task 8. */
export function observeUser(auth: Auth, onChange: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, onChange)
}

type BootstrapResult = { householdId: string }

/**
 * Chama o bootstrap da casa no servidor. **Toda abertura do app chama**, não
 * só o primeiro login: a function é idempotente de propósito, e é assim que
 * uma falha se conserta sozinha na abertura seguinte (decisão 42).
 *
 * A falha sobe para quem chamou. Engolir aqui devolveria o app ao estado que a
 * callable existe para evitar: usuário autenticado, sem casa, sem nada que o
 * repare.
 */
export async function bootstrapHousehold(functions: Functions): Promise<string> {
  const call = httpsCallable<void, BootstrapResult>(functions, 'bootstrapHousehold')
  const { data } = await call()

  if (typeof data?.householdId !== 'string') {
    throw new Error('bootstrapHousehold: resposta sem householdId.')
  }

  return data.householdId
}
